// @file Shared SafeHtml helpers (pure string; no DOM)

const escapeHtml = (str: string): string => {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Opaque HTML that has already been escaped (or is provably static).
 * Runtime object so `html` can pass nested instances through without re-escaping;
 * a branded string would be erased at runtime and double-escape.
 *
 * Uses a Symbol slot (not a `#` private field) so Vue's reactive Proxy can still
 * call `toString()` when the instance is nested inside a reactive row object.
 */
const safeHtmlValue = Symbol('SafeHtml.value')

class SafeHtml {
  declare readonly [safeHtmlValue]: string

  constructor(html: string) {
    Object.defineProperty(this, safeHtmlValue, { value: html, enumerable: false })
  }

  toString(): string {
    return this[safeHtmlValue]
  }
}

const isSafeHtml = (value: unknown): value is SafeHtml => value instanceof SafeHtml

/**
 * Tagged template that HTML-escapes every interpolated value (and passes nested
 * `SafeHtml` through unchanged).
 *
 * Safe only for the interpolation contexts this helper is used in today: element
 * text and quoted attribute values. It does not neutralize `javascript:` in an
 * `href`, and it does not protect unquoted attributes (`<div class=${x}>`).
 * Prefer static hrefs and always-quoted attributes.
 */
const html = (strings: TemplateStringsArray, ...values: unknown[]): SafeHtml => {
  let result = strings[0] ?? ''
  for (let i = 0; i < values.length; i++) {
    const value = values[i]
    result += (isSafeHtml(value) ? value.toString() : escapeHtml(String(value))) + (strings[i + 1] ?? '')
  }
  return new SafeHtml(result)
}

/** Escape hatch for markup that is provably static or already escaped. Must carry a justification. */
const trustedHtml = (value: string, _why: string): SafeHtml => new SafeHtml(value)

export { escapeHtml, html, isSafeHtml, trustedHtml }
export type { SafeHtml }
